import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import PrivateRoute from "./components/PrivateRoute";
import PublicRoute from "./components/PublicRoute";
import LoginPage from "./pages/login";
import { useSelector, useDispatch } from "react-redux";
import { getUserRole } from "./store/userRole";
import WelcomePage from "./pages/WelcomePage/WelcomePage";
import BeforeLoginHeader from "./components/Layout/BeforeLoginHeader";
import RegisterPage from "./pages/register";
import InvalidPermissionsDialog from "./components/InvalidPermissions/InvalidPermissonPopUp";
import { getInvalidDialogProp, setInvalidDialog } from "./store/invalidDialog";
import OtpComponent from "./components/Auth/OtpComponent";
import Loader from "./components/Loader";
import { useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import Layout from "./components/Layout";
// eslint-disable-next-line no-unused-vars
import HackTimer from "./script/HackTimer";
import StudentRoutes from "./routes/StudentRoutes";
import VerifyStudentSession from "./pages/VerifyStudentSession/VerifyStudentSession";
const Redirection = lazy(() => import("./pages/Redirection/Redirect"));
const DiagnosticSetupPage = lazy(
  () => import("./pages/DiagnosticSetup/DiagnosticSetupPage"),
);
const DiagnosticTestPage = lazy(
  () => import("./pages/DiagnosticTest/DiagnosticTestPage"),
);
const AdminDashboard = lazy(
  () => import("./pages/AdminOrTeacherPages/AdminDashboard"),
);
const AdminOrTeacterRoutes = lazy(
  () => import("./routes/AdminOrTeacherRoutes"),
);
const WatcherRoutes = lazy(() => import("./routes/WatcherRoutes"));

const queryClient = new QueryClient();

// Component that uses useLocation and needs to be inside BrowserRouter
const AppRoutes = () => {
  const roleOfTheUser = useSelector(getUserRole);
  const location = useLocation();

  // Check if current path is a student route
  const isStudentRoute =
    location.pathname.startsWith("/student") ||
    location.pathname.startsWith("/notifications/list");

  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicRoute>
            <WelcomePage />
          </PublicRoute>
        }
      />
      <Route
        path="/api/v1/home/meetingendlink"
        element={<PrivateRoute Component={Redirection} />}
      />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <BeforeLoginHeader isLogin={true}>
              <LoginPage />
            </BeforeLoginHeader>
          </PublicRoute>
        }
      />
      <Route
        path="/verifysession/student/:token"
        element={<VerifyStudentSession />}
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <BeforeLoginHeader isLogin={false}>
              <RegisterPage />
            </BeforeLoginHeader>
          </PublicRoute>
        }
      />
      <Route
        path="/otp"
        element={
          <PublicRoute>
            <BeforeLoginHeader isLogin={false}>
              <OtpComponent />
            </BeforeLoginHeader>
          </PublicRoute>
        }
      />
      <Route
        path="/sat-diagnostic-setup"
        element={
          <BeforeLoginHeader isLogin={false}>
            <DiagnosticSetupPage />
          </BeforeLoginHeader>
        }
      />
      <Route
        path="/sat-diagnostic-test"
        element={
          <BeforeLoginHeader isLogin={false}>
            <DiagnosticTestPage />
          </BeforeLoginHeader>
        }
      />

      {/* Admin Dashboard Route - only for admin roles */}
      <Route
        path="/admin/dashboard"
        element={
          <Layout isSecondSidebarOpen={false}>
            <PrivateRoute Component={AdminDashboard} />
          </Layout>
        }
      />

      {/*
          ================================================
                  Students defined URLs/Routes
          ================================================
    */}
      {(roleOfTheUser === "Student" ||
        (roleOfTheUser === "" && isStudentRoute)) && (
        <Route
          path="*"
          element={
            <Suspense fallback={<Loader />}>
              <StudentRoutes />
            </Suspense>
          }
        />
      )}
      {/*
          ================================================
                Watcher defined URLs/Routes
          ================================================
    */}
      {roleOfTheUser && roleOfTheUser === "Watcher" && (
        <Route
          path="*"
          element={
            <Suspense fallback={<Loader />}>
              <WatcherRoutes />
            </Suspense>
          }
        />
      )}
      {/*
          ================================================
                Admin defined URLs/Routes
          ================================================
    */}
      {roleOfTheUser &&
        roleOfTheUser !== "Student" &&
        roleOfTheUser !== "Watcher" && (
          <Route
            path="*"
            element={
              <Suspense fallback={<Loader />}>
                <AdminOrTeacterRoutes />
              </Suspense>
            }
          />
        )}
    </Routes>
  );
};

const App = () => {
  const isDialogOpen = useSelector(getInvalidDialogProp);
  const dispatch = useDispatch();

  const setInValidDialogProps = (val) => {
    dispatch(setInvalidDialog(val));
  };

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastContainer
          position="bottom-center"
          autoClose={1500}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <InvalidPermissionsDialog
          open={isDialogOpen}
          setOpen={setInValidDialogProps}
        />
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
